<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :width="big ? 24 : width"
    :height="big ? 24 : height || width"
    :viewBox="viewBox"
    :aria-label="ariaLabel || name"
    role="presentation"
    class="custom-icon-component"
    :class="{ active: active, 'has-hover': hasHoverState, 'parent-hovered': hasHoverWithParent, disabled: disabled }"
    :style="style"
  >
    <g :fill="color">
      <keep-alive>
        <component :is="name" />
      </keep-alive>
    </g>
  </svg>
</template>

<script lang="ts">
/**
 * Creates a configuration wrapper for icons
 * and import them according to the prop name that should
 * match the component name (kebab-cased)
 */
import { defineComponent, computed } from "vue";

export default defineComponent({
  name: "VdCustomIcon",

  props: {
    /**
     * Name of the icon
     */
    name: {
      type: String,
      default: "placeholder-icon",
    },

    /**
     * Aria Label (which is also the title of the icon)
     */
    ariaLabel: {
      type: String,
      default: null,
    },

    /**
     * Width of the icon - can be used alone
     */
    width: {
      type: [Number, String],
      default: 20,
    },

    /**
     * Possibility to specify the height, only necessary for non square icons
     */
    height: {
      type: [Number, String],
      default: undefined,
    },

    /**
     * Define the viewBox manually (rare cases)
     */
    box: {
      type: String,
      default: "0 0 20 20",
    },

    /**
     * If it is a big icon (24px)
     */
    big: {
      type: Boolean,
      default: false,
    },

    /**
     * This should only to be used if you want to enforce the color of mono-color icons
     */
    color: {
      type: String,
      default: null,
    },

    /**
     * Is the icon in disabled state
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Is the icon in an active state
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Does the icon has the possibility to be hovered (in which case, the active state will be present on hover)
     */
    hasHoverState: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, has a hover state when the parent is hovered.
     */
    hasHoverWithParent: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const viewBox = computed(() => {
      if (props.big) {
        return "0 0 24 24";
      }

      return props.box;
    });

    const style = computed(() => {
      if (!props.width) {
        return "";
      }

      return `min-width: ${props.width}`;
    });

    return {
      viewBox,
      style,
    };
  },
});
</script>

<style lang="scss">
.custom-icon-component {
  display: inline-block;
  vertical-align: baseline;

  /* Normal State */
  fill: var(--vd-base-primary-text-color);

  .secondary-color {
    fill: var(--voodootune-background-color);
  }

  /* Hover and Active State */
  &.has-hover:hover,
  &.has-hover.hover,
  &.active {
    fill: $intense-blue;

    .secondary-color {
      fill: var(--voodootune-highlight-color);
    }
  }

  /* Disabled state */
  &.disabled {
    fill: var(--vd-color-grey-light-1);

    .secondary-color {
      fill: var(--voodootune-background-color);
    }
  }

  /* Having a difference with the selected state */
  &.has-hover:hover,
  &.has-hover.hover {
    opacity: 0.7;
  }
}

*:hover > .custom-icon-component.has-hover.parent-hovered,
*:hover > span > .custom-icon-component.has-hover.parent-hovered {
  fill: $intense-blue;

  .secondary-color {
    fill: var(--voodootune-highlight-color);
  }
}
</style>
